import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { AuthContext } from "@/App";
import { Package, Plus, Pencil, Trash2, AlertTriangle, X, ChevronUp, ChevronDown } from "lucide-react";
import { inr } from "@/lib/ecotile";
import { searchCatalogue } from "@/lib/ecotileCatalogue";
import { ProductGlyph, PvcBagIcon, hexForColour } from "@/components/EcotileGlyph";
import { EcoSearchBox } from "@/components/EcotileHeaderTools";
import inFlag from "@/components/icons/flags/in.svg";
import aeFlag from "@/components/icons/flags/ae.svg";
import auFlag from "@/components/icons/flags/au.svg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCATIONS = ["India", "UAE", "Australia"];
const FLAGS = { India: inFlag, UAE: aeFlag, Australia: auFlag };
const RM_UNITS = ["kg", "tons"];
const STATUS_ORDER = { "In stock": 0, "Low": 1, "Out": 2 };

const STATUS_TONE = {
  "In stock": "text-emerald-600 dark:text-emerald-400 border-emerald-500/40 bg-emerald-500/5",
  "Low": "text-yellow-700 dark:text-yellow-400 border-yellow-500/40 bg-yellow-500/5",
  "Out": "text-red-600 dark:text-red-400 border-red-500/40 bg-red-500/5",
};
function StatusPill({ s }) {
  return <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border rl-pill leading-none whitespace-nowrap ${STATUS_TONE[s] || ""}`}>{s === "Out" && <AlertTriangle className="w-2.5 h-2.5" />}{s}</span>;
}
const num = (v) => (v === "" || v == null ? 0 : parseFloat(v) || 0);
const inNum = (v) => Number(v || 0).toLocaleString("en-IN");

// ── receive / edit a lot ─────────────────────────────────────────────────────
function LotForm({ store, initial, onClose, onSaved }) {
  const editing = !!(initial && initial.id);
  const [f, setF] = useState(() => store === "fg" ? {
    item_code: initial?.item_code || "", description: initial?.description || "",
    colour: initial?.colour || "", hex: initial?.hex || "", thickness: initial?.thickness || "", texture: initial?.texture || "",
    location: initial?.location || "India", unit: initial?.unit || "tiles",
    qty_on_hand: initial?.qty_on_hand ?? "", batch_no: initial?.batch_no || "",
    unit_cost: initial?.unit_cost ?? "", low_threshold: initial?.low_threshold ?? "",
  } : {
    material: initial?.material || "PVC", grade: initial?.grade || "", colour: initial?.colour || "",
    supplier: initial?.supplier || "", location: initial?.location || "India", unit: initial?.unit || "kg",
    qty_on_hand: initial?.qty_on_hand ?? "", batch_no: initial?.batch_no || "",
    unit_cost: initial?.unit_cost ?? "", low_threshold: initial?.low_threshold ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [sku, setSku] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const skuResults = useMemo(() => (store === "fg" && sku ? searchCatalogue(sku, null, 12) : []), [store, sku]);
  const pickSku = (it) => {
    setF((s) => ({ ...s, item_code: it.code, description: it.name, colour: it.colour || "", hex: it.hex || "", thickness: it.thk || "", texture: it.tex || "" }));
    setSku("");
  };
  const save = async () => {
    const body = { ...f, qty_on_hand: num(f.qty_on_hand), unit_cost: num(f.unit_cost), low_threshold: num(f.low_threshold) };
    if (store === "fg" && !body.description.trim()) { toast.error("Pick a SKU or type a description"); return; }
    if (store === "rm" && !body.grade.trim() && !body.colour.trim()) { toast.error("Enter a grade or colour"); return; }
    setBusy(true);
    try {
      if (editing) await axios.patch(`${API}/ecotile/${store}/${initial.id}`, body);
      else await axios.post(`${API}/ecotile/${store}`, body);
      toast.success(editing ? "Lot updated" : "Stock received");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const inp = "w-full h-9 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm outline-none focus:border-[#E6B800]";
  const Field = ({ label, children, span }) => (
    <label className={span ? "col-span-2 block" : "block"}>
      <span className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 overflow-auto" style={{ background: "rgba(0,0,0,.45)" }} onClick={onClose}>
      <div className="rl-frost w-full max-w-[560px] rounded-2xl border border-[hsl(var(--border))] shadow-2xl mt-10" onClick={(e) => e.stopPropagation()} data-testid="eco-lot-form">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[hsl(var(--border))]">
          <div className="font-bold text-[15px]">{editing ? "Edit lot" : store === "fg" ? "Receive finished stock" : "Receive raw material"}</div>
          <button onClick={onClose} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {store === "fg" ? (
            <>
              {!editing && (
                <div className="relative">
                  <input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="Find a SKU — dark grey, ER7, E500/7…" className={inp} />
                  {skuResults.length > 0 && (
                    <div className="absolute z-[70] left-0 right-0 mt-1 max-h-52 overflow-auto rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-xl rl-frost">
                      {skuResults.map((it) => (
                        <button key={it.code} type="button" onClick={() => pickSku(it)} className="w-full text-left px-2.5 py-1.5 flex items-center gap-2 hover:bg-[#FFCC00]/[0.09]">
                          <ProductGlyph code={it.code} hex={it.hex} tex={it.tex} size={16} />
                          <span className="text-[12px] flex-1 truncate">{it.name}</span>
                          <span className="text-[10px] font-mono text-[hsl(var(--muted-foreground))]">{it.code}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <ProductGlyph code={f.item_code} hex={f.hex} tex={f.texture} size={26} />
                <input value={f.description} onChange={set("description")} placeholder="Item description" className={inp} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Item code"><input value={f.item_code} onChange={set("item_code")} className={inp} /></Field>
                <Field label="Colour"><input value={f.colour} onChange={set("colour")} className={inp} /></Field>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Grade"><input value={f.grade} onChange={set("grade")} placeholder="PVC-K67 rigid" className={inp} /></Field>
              <Field label="Colour"><input value={f.colour} onChange={set("colour")} placeholder="Dark Grey" className={inp} /></Field>
              <Field label="Supplier" span><input value={f.supplier} onChange={set("supplier")} className={inp} /></Field>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Location"><select value={f.location} onChange={set("location")} className={inp}>{LOCATIONS.map((l) => <option key={l}>{l}</option>)}</select></Field>
            <Field label="Unit">{store === "rm"
              ? <select value={f.unit} onChange={set("unit")} className={inp}>{RM_UNITS.map((u) => <option key={u}>{u}</option>)}</select>
              : <input value={f.unit} onChange={set("unit")} className={inp} />}</Field>
            <Field label="Quantity on hand"><input value={f.qty_on_hand} onChange={set("qty_on_hand")} inputMode="decimal" className={inp} /></Field>
            <Field label="Batch #"><input value={f.batch_no} onChange={set("batch_no")} placeholder="fresh batch per shade" className={inp} /></Field>
            <Field label="Unit cost (₹)"><input value={f.unit_cost} onChange={set("unit_cost")} inputMode="decimal" className={inp} /></Field>
            <Field label="Low-stock threshold"><input value={f.low_threshold} onChange={set("low_threshold")} inputMode="decimal" className={inp} /></Field>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-[hsl(var(--border))]">
          <button onClick={onClose} className="h-9 px-4 rounded-full text-[13px] font-medium border border-[hsl(var(--border))]">Cancel</button>
          <button onClick={save} disabled={busy} className="h-9 px-5 rounded-full text-[13px] font-bold disabled:opacity-50" style={{ background: "#FFCC00", color: "#2b2500" }}>{editing ? "Save" : "Receive"}</button>
        </div>
      </div>
    </div>
  );
}

// ── summary strip ────────────────────────────────────────────────────────────
function StoreStat({ big, label, y }) {
  return (
    <div className="px-3 py-1">
      <div className="text-[18px] font-extrabold leading-tight" style={{ color: y ? "#FFCC00" : "#fff" }}>{big}</div>
      <div className="text-[10px] font-medium text-white/60 whitespace-nowrap mt-0.5">{label}</div>
    </div>
  );
}
function SummaryStrip({ store, s, hideVal }) {
  const b = store === "fg" ? s?.fg : s?.rm;
  if (!b) return null;
  const money = (v) => (hideVal || v == null ? "•••" : inr(v));
  return (
    <div className="rounded-xl px-3 py-2 flex flex-wrap items-stretch gap-x-1 gap-y-2" style={{ background: "#2b2b2b" }} data-testid="eco-store-summary">
      <div className="text-[9.5px] font-extrabold tracking-widest text-[#FFCC00] uppercase self-center px-2">{store === "fg" ? "FG" : "RM"}</div>
      <StoreStat big={b.lots} label={store === "fg" ? "SKU lots" : "material lots"} />
      <StoreStat big={inNum(b.on_hand)} label={`on hand${store === "rm" ? " (kg)" : " (tiles)"}`} y />
      <StoreStat big={inNum(b.available)} label="available (net reserved)" />
      <StoreStat big={<span className={b.attention ? "text-orange-300" : ""}>{b.attention}</span>} label="low / out" />
      <StoreStat big={money(b.value)} label="carrying value" />
    </div>
  );
}

// ── main ─────────────────────────────────────────────────────────────────────
export default function EcotileStores() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "Admin";
  const canManage = isAdmin || !!user?.permissions?.can_manage_ecotile_orders;
  const canViewValue = isAdmin || !!user?.permissions?.can_view_ecotile_value;

  const [store, setStore] = useState("fg");     // "fg" | "rm"
  const [location, setLocation] = useState("India"); // default to HQ
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [colourF, setColourF] = useState("all");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [summary, setSummary] = useState(null);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);        // null | "new" | <lot>

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sm, ls] = await Promise.all([
        axios.get(`${API}/ecotile/stores/summary`, { params: { location } }),
        axios.get(`${API}/ecotile/${store}`, { params: { location, q } }),
      ]);
      setSummary(sm.data); setLots(ls.data.lots || []);
    } catch (e) { toast.error("Could not load stores"); }
    finally { setLoading(false); }
  }, [store, location, q]);
  useEffect(() => { load(); }, [load]);

  const del = async (lot) => {
    try { await axios.delete(`${API}/ecotile/${store}/${lot.id}`); toast.success("Lot removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const colourOpts = useMemo(() => Array.from(new Set(lots.map((l) => l.colour).filter(Boolean))).sort(), [lots]);
  const nameOf = (l) => store === "fg" ? (l.description || l.item_code || "") : `${l.grade || ""} ${l.colour || ""}`.trim();

  const rows = useMemo(() => {
    let r = lots.filter((l) => (statusF === "all" || l.status === statusF) && (colourF === "all" || l.colour === colourF));
    const acc = {
      name: (l) => nameOf(l).toLowerCase(), location: (l) => l.location || "",
      qty_on_hand: (l) => num(l.qty_on_hand), qty_reserved: (l) => num(l.qty_reserved),
      qty_available: (l) => num(l.qty_available), batch_no: (l) => l.batch_no || "",
      unit_cost: (l) => num(l.unit_cost), value: (l) => num(l.value),
      status: (l) => STATUS_ORDER[l.status] ?? 9,
    }[sort.key] || ((l) => nameOf(l).toLowerCase());
    r = [...r].sort((a, b) => {
      const va = acc(a), vb = acc(b);
      const c = va < vb ? -1 : va > vb ? 1 : 0;
      return sort.dir === "asc" ? c : -c;
    });
    return r;
  }, [lots, statusF, colourF, sort, store]);

  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const SortTh = ({ k, label, align = "left" }) => (
    <th className={`font-semibold px-2 py-2 cursor-pointer select-none hover:text-[hsl(var(--foreground))] text-${align}`} onClick={() => toggleSort(k)}>
      <span className={`inline-flex items-center gap-0.5 ${align === "right" ? "flex-row-reverse" : ""}`}>{label}
        {sort.key === k && (sort.dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}</span>
    </th>
  );

  const tabBtn = (v, label, Icon) => (
    <button onClick={() => setStore(v)} className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-bold border transition-colors ${store === v ? "rl-pressed bg-[#FFCC00] border-[#E6B800] text-[#2b2500]" : "rl-pill bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>
      {Icon}{label}
    </button>
  );

  // country slider (borrowed from Stock Center's carved pill rail) — India is HQ
  const locPill = (v) => `inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11.5px] font-semibold transition-colors ${location === v ? "rl-thumb bg-[hsl(var(--card))] text-[hsl(var(--foreground))]" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`;
  const Flag = ({ l }) => <img src={FLAGS[l]} alt="" width={15} height={15} className="rounded-[2px] object-cover" style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,.12)" }} />;

  return (
    <div className="space-y-3" data-testid="eco-stores">
      {/* country slider — India (HQ) separated from the overseas locations */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Location</span>
        <div className="inline-flex items-center rl-carved bg-[hsl(var(--muted))]/50 rounded-full p-1 gap-0.5 border border-[hsl(var(--border))]" role="tablist" aria-label="Location">
          <button onClick={() => setLocation("India")} className={locPill("India")} title="Main factory">
            <Flag l="India" /> India
            <span className="text-[8.5px] font-extrabold px-1 py-px rounded-full" style={{ background: "#FFCC00", color: "#2b2500" }}>HQ</span>
          </button>
          <span className="w-px h-5 bg-[hsl(var(--border))] mx-1" aria-hidden="true" />
          <button onClick={() => setLocation("UAE")} className={locPill("UAE")}><Flag l="UAE" /> UAE</button>
          <button onClick={() => setLocation("Australia")} className={locPill("Australia")}><Flag l="Australia" /> Australia</button>
          <span className="w-px h-5 bg-[hsl(var(--border))] mx-1" aria-hidden="true" />
          <button onClick={() => setLocation("all")} className={locPill("all")}>All</button>
        </div>
      </div>

      {/* store tabs + search + receive */}
      <div className="flex items-center gap-2 flex-wrap">
        {tabBtn("fg", "FG Storage", <Package className="w-3.5 h-3.5" />)}
        {tabBtn("rm", "RM Store", <PvcBagIcon className="w-4 h-4" />)}
        <div className="ml-auto flex items-center gap-2">
          <EcoSearchBox value={q} onChange={setQ} width="w-40"
                        placeholder={store === "fg" ? "Search SKU / colour…" : "Search grade / supplier…"} />
          {canManage && (
            <button onClick={() => setForm("new")} data-testid="eco-store-receive" className="inline-flex items-center gap-1 h-9 px-3.5 rounded-full text-[12.5px] font-bold rl-lit-btn hover:brightness-105" style={{ background: "#FFCC00", color: "#2b2500" }}>
              <Plus className="w-3.5 h-3.5" /> Receive
            </button>
          )}
        </div>
      </div>

      {/* filters */}
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Filter</span>
        {["all", "In stock", "Low", "Out"].map((s) => (
          <button key={s} onClick={() => setStatusF(s)} className={`px-2.5 py-1 rounded-full font-semibold border transition-colors ${statusF === s ? "rl-pressed bg-[#2b2b2b] border-[#2b2b2b] text-white dark:bg-white dark:border-white dark:text-[#1a1a1a]" : "rl-pill bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>{s === "all" ? "All status" : s}</button>
        ))}
        <select value={colourF} onChange={(e) => setColourF(e.target.value)} className="h-7 px-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[11px] rl-pill">
          <option value="all">All colours</option>
          {colourOpts.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {(statusF !== "all" || colourF !== "all") && <button onClick={() => { setStatusF("all"); setColourF("all"); }} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] underline">clear</button>}
      </div>

      <SummaryStrip store={store} s={summary} hideVal={!canViewValue} />

      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] rl-tile rl-glass-edge overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[720px]">
            <thead className="rl-thead">
              <tr className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                <SortTh k="name" label={store === "fg" ? "SKU" : "Material"} />
                <SortTh k="location" label="Location" />
                <SortTh k="qty_on_hand" label="On hand" align="right" />
                <SortTh k="qty_reserved" label="Reserved" align="right" />
                <SortTh k="qty_available" label="Available" align="right" />
                <SortTh k="batch_no" label="Batch" />
                {canViewValue && <SortTh k="unit_cost" label="Unit cost" align="right" />}
                {canViewValue && <SortTh k="value" label="Value" align="right" />}
                <SortTh k="status" label="Status" />
                {canManage && <th className="px-2 py-2" />}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="px-4 py-12 text-center text-[hsl(var(--muted-foreground))]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={12} className="px-4 py-12 text-center">
                  <div className="text-[14px] font-semibold mb-1">{lots.length === 0 ? (store === "fg" ? "No finished stock here" : "No raw material here") : "No lots match the filters"}</div>
                  <div className="text-[hsl(var(--muted-foreground))] text-sm">{lots.length === 0 ? (canManage ? "Use Receive to add a lot." : "Stock will appear here once received.") : "Adjust the status or colour filter."}</div>
                </td></tr>
              ) : rows.map((lot) => (
                <tr key={lot.id} className="border-b border-[hsl(var(--border))]/60 hover:bg-[hsl(var(--muted))]/20">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {store === "fg" ? <ProductGlyph code={lot.item_code} hex={lot.hex} tex={lot.texture} /> : <ProductGlyph kind="tile" colour={lot.colour} />}
                      <div className="min-w-0">
                        <div className="text-[12px] font-medium truncate max-w-[280px]" title={nameOf(lot)}>{store === "fg" ? lot.description : `${lot.grade || "PVC"}${lot.colour ? " · " + lot.colour : ""}`}</div>
                        <div className="text-[10px] font-mono text-[hsl(var(--muted-foreground))] truncate">{store === "fg" ? lot.item_code : lot.supplier}{lot.seeded ? " · seed" : ""}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-[12px]">{lot.location}</td>
                  <td className="px-2 py-2 text-right text-[12px] tabular-nums">{inNum(lot.qty_on_hand)} <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{lot.unit}</span></td>
                  <td className="px-2 py-2 text-right text-[12px] tabular-nums text-[hsl(var(--muted-foreground))]">{inNum(lot.qty_reserved)}</td>
                  <td className="px-2 py-2 text-right text-[12px] tabular-nums font-semibold">{inNum(lot.qty_available)}</td>
                  <td className="px-2 py-2 text-[11px] text-[hsl(var(--muted-foreground))]">{lot.batch_no || "—"}</td>
                  {canViewValue && <td className="px-2 py-2 text-right text-[12px] tabular-nums">{lot.unit_cost == null ? "•••" : inr(lot.unit_cost)}</td>}
                  {canViewValue && <td className="px-2 py-2 text-right text-[12px] tabular-nums">{lot.value == null ? "•••" : inr(lot.value)}</td>}
                  <td className="px-2 py-2"><StatusPill s={lot.status} /></td>
                  {canManage && (
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => setForm(lot)} className="w-7 h-7 rounded-full rl-well inline-flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => del(lot)} className="w-7 h-7 rounded-full rl-well inline-flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-red-500" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-[11px] text-[hsl(var(--muted-foreground))] px-1">
        {rows.length} {store === "fg" ? "finished-stock" : "raw-material"} lots{location !== "all" ? ` in ${location}` : " across all locations"}{lots.length !== rows.length ? ` (${lots.length} before filters)` : ""}. FG SKUs seed from the price-list catalogue; RM rows are placeholders until real material data is loaded.
      </div>

      {form && <LotForm store={store} initial={form === "new" ? null : form} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}
